// @flow

import * as Cookie from 'app/packages/cookie-utils';
import CONST from 'app/common/lib/Const';
import FbUtil from 'app/common/lib/FbUtil';
import focusUtil from 'app/common/lib/focusUtil';
import invariant from 'invariant';
import loginUtil from 'app/www-unauth/lib/loginUtil';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import User from 'app/common/lib/User';
import { logCustomTraceEvent } from 'app/common/lib/TraceLogger/logTraceEvent';
import { MultiTreatmentExp } from 'app/common/lib/experiment';
import { type RequestContextType } from 'app/common/react/components/RequestContext';
import {
  fbAutoLoginNotAuthorized,
  fbAutoLoginAuthorizationExpired,
  fbAutoLoginConnected,
  fbAutoLoginUnknown,
} from 'app/www-unauth/redux/actions/facebookAutoLoginActions';

const authHandshakeLoader = () =>
  import(/* webpackChunkName: "AuthHandshakeCommon" */ 'app/common/lib/AuthHandshake');
const AutologinUtilLoader = () =>
  import(/* webpackChunkName: "AutologinUtilCommon" */ 'app/common/lib/AutologinUtil/AutologinUtil');
const FacebookLoader = () =>
  import(/* webpackChunkName: "FacebookCommon" */ 'app/common/lib/Facebook');

function attemptBrowserCredentialsAutoLogin(location: Object) {
  const browserCredentials: ?Object = window.navigator.credentials;
  if (browserCredentials && !Cookie.get('logged_out')) {
    loginUtil.retrieveLoginCredentialsFromBrowser().then(
      (creds) => {
        trackRegisterAction('navigatorCredentials.retrieve.success.pure_react');
        if (!creds) {
          return;
        }

        const csrfToken = Cookie.get('csrftoken');
        const appState = focusUtil.windowIsInFocus ? 'active' : 'background';
        loginUtil.attemptLoginWithBrowserCredentials(creds, csrfToken, appState).then(
          (resp) => {
            const defaultRedirectUrl = `${location.pathname}${location.search}`;
            const nextUrl =
              (location && location.query && location.query.next) || defaultRedirectUrl;
            loginUtil.handleRedirect(nextUrl);
          },
          (error) => {
            // Fail silently.
          },
        );
      },
      (error) => {
        // Fail silently
        trackRegisterAction('navigatorCredentials.retrieve.failure.pure_react');
      },
    );
  }
}

function attemptFbGoogleAutologin(
  dispatch: Function,
  locale: string,
  requestContext: RequestContextType,
  location: Object,
  gplusAutologinMigrationGroup: MultiTreatmentExp,
  unauthPageWrapperOptimizationExp: MultiTreatmentExp,
) {
  trackRegisterAction('unauth_web.attempt_autologin');
  logCustomTraceEvent({ name: 'UnauthFacebookAutologinStartEvent' });
  Promise.all([FacebookLoader(), AutologinUtilLoader()]).then(
    ([{ default: Facebook }, { fbAutologinCallback, googleAutologin }]) => {
      // Facebook autologin
      Facebook.ensureInit(
        (_fb) => {
          logCustomTraceEvent({ name: 'UnauthFacebookAutologinScriptLoadEvent' });
          trackRegisterAction('unauth_web.facebook_autologin.get_login_status.attempt');
          _fb.getLoginStatus((response) => {
            trackRegisterAction('unauth_web.facebook_autologin.get_login_status.response');
            if (response.status === 'connected') {
              trackRegisterAction(
                'unauth_web.facebook_autologin.get_login_status.response_connected',
              );
              logCustomTraceEvent({ name: 'UnauthFacebookAutologinSuccessEvent' });
              dispatch(fbAutoLoginConnected());
            }
            fbAutologinCallback(response, 'pure_react', false);
            if (response.status === 'not_authorized') {
              trackRegisterAction(
                'unauth_web.facebook_autologin.get_login_status.response_not_authorized',
              );
              dispatch(fbAutoLoginNotAuthorized());
            } else if (response.status === 'authorization_expired') {
              trackRegisterAction(
                'unauth_web.facebook_autologin.get_login_status.response_authorization_expired',
              );
              dispatch(fbAutoLoginAuthorizationExpired());
            } else {
              dispatch(fbAutoLoginUnknown());
            }
          });
        },
        User,
        locale,
        unauthPageWrapperOptimizationExp,
      );
      // Google autologin
      trackRegisterAction('autologin.create_google_autologin_button.number_3');
      const div = document.createElement('div');
      div.style.left = '-99999px';
      div.style.opacity = '0';
      div.style.position = 'fixed';
      div.style.top = '-999999px';
      invariant(document.body, 'for flow');
      document.body.appendChild(div);
      googleAutologin(
        div,
        CONST.GEN.templateConst.settings,
        'pure_react',
        location,
        false,
        gplusAutologinMigrationGroup,
      );
    },
  );
}

function attemptCrossDomainAutologin() {
  const handshakeDomains = CONST.GEN.templateConst.settings.CORS_HANDSHAKE_DOMAINS;
  if (handshakeDomains.indexOf(window.location.origin) !== -1 && !Cookie.get('logged_out')) {
    authHandshakeLoader().then(({ default: authHandshake }) => {
      authHandshake.verifyLoggedInStatus();
    });
  }
}

export default {
  attemptLogin(requestContext: RequestContextType, dispatch: Function, location: Object) {
    attemptBrowserCredentialsAutoLogin(location);
    attemptCrossDomainAutologin();

    if (!requestContext.isAuthenticated) {
      if (Cookie.get(PCONST.COOKIE_FB_AUTO_LOGIN_DISABLED)) {
        trackRegisterAction('web_previously_logged_out.true');
      } else {
        trackRegisterAction('web_previously_logged_out.false');
      }

      /*
       * Even if the user has the autologin-disable cookie set, proceed with the autologin
       * calls to see if the user is connected
       */

      const gplusAutologinMigrationGroup = new MultiTreatmentExp(
        requestContext,
        'web_unauth_gplus_autologin_migration',
      );

      const unauthPageWrapperOptimizationExp = new MultiTreatmentExp(
        requestContext,
        'unauth_web_page_wrapper_optimize_2',
      );

      if (requestContext.userAgent.isMobile || requestContext.userAgent.isTablet) {
        setTimeout(() => {
          attemptFbGoogleAutologin(
            dispatch,
            FbUtil.getLocaleString(requestContext.locale),
            requestContext,
            location,
            gplusAutologinMigrationGroup,
            unauthPageWrapperOptimizationExp,
          );
        }, 100);
      } else {
        attemptFbGoogleAutologin(
          dispatch,
          FbUtil.getLocaleString(requestContext.locale),
          requestContext,
          location,
          gplusAutologinMigrationGroup,
          unauthPageWrapperOptimizationExp,
        );
      }
    }
  },
};
